import React, { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import Checkbox from "@/components/ui/checkbox";

import {  CheckCircle, AlertCircle } from "lucide-react";
import { ImageUpload } from "@/components/ui/ImageUpload";
import {  Package } from "lucide-react";
import CategoryService, { ICategory } from "@/services/products/categoryService";
import POSService, { IPOS } from "@/services/pos/posService";

/**
 * Interfaz para los datos del formulario de producto
 */
export interface IProductFormData {
  nombre: string;
  descripcion: string;
  precio: number;
  categoria: string;
  imagen?: string;
  disponible: boolean;
  type: 'single' | 'pack';
  pack_size: number | null;
  imageFile?: File; // Archivo de imagen para subir
  posIds?: number[];
}

/**
 * Props del componente ProductForm
 */
interface ProductFormProps {
  onSubmit: (data: IProductFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<IProductFormData>;
}

/**
 * Schema de validación para el formulario de producto
 */
const productSchema = z.object({
  nombre: z.string().min(1, "El nombre del producto es requerido"),
  descripcion: z.string().min(1, "La descripción es requerida"),
  precio: z.number().min(1, "El precio debe ser mayor a 0"),
  categoria: z.string().min(1, "La categoría es requerida"),
  imagen: z.string().optional(),
  disponible: z.boolean(),
  type: z.enum(['single', 'pack'], { required_error: "El tipo de producto es requerido" }),
  pack_size: z.number().nullable().optional().refine((val) => val === null || (val !== undefined && val > 0), {
    message: "El tamaño del pack debe ser un número positivo",
  }),
  imageFile: z.any().optional(), // Archivo de imagen (no se valida en el schema)
}).superRefine((data, ctx) => {
  if (data.type === 'pack' && (data.pack_size === null || data.pack_size === undefined || data.pack_size <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "El tamaño del pack es requerido y debe ser mayor a 0 para productos 'pack'",
      path: ['pack_size'],
    });
  }
});

/**
 * Componente de formulario para crear/editar productos
 *
 * Incluye validación con Zod, manejo de estados y feedback visual
 * Optimizado para dispositivos móviles con campos táctiles
 */
const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  isLoading = false,
  initialData = {},
}) => {
  // Estados del formulario
  const [formData, setFormData] = useState<IProductFormData>({
    nombre: initialData.nombre || "",
    descripcion: initialData.descripcion || "",
    precio: initialData.precio || 0,
    // Asegura que categoria sea string (id)
    categoria: initialData.categoria ? String(initialData.categoria) : "",
    imagen: initialData.imagen || "",
    disponible: initialData.disponible ?? true,
    type: initialData.type || 'single',
    pack_size: initialData.pack_size ?? null,
    posIds: initialData.posIds || [],
  });

  // Estado para el archivo de imagen
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Estado para categorías dinámicas
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  // Estado para POS
  const [posList, setPosList] = useState<IPOS[]>([]);
  const [loadingPOS, setLoadingPOS] = useState<boolean>(true);
  const [posError, setPosError] = useState<string | null>(null);

  useEffect(() => {
    console.log("formData", formData);
  }, [formData]);

  useEffect(() => {
    console.log("categories", categories);
  }, [categories]);

  useEffect(() => {
    setLoadingCategories(true);
    CategoryService.getAllCategories()
      .then((data: ICategory[]) => {
        setCategories(data);
        setLoadingCategories(false);
      })
      .catch(() => {
        setCategoriesError("Error al cargar categorías");
        setLoadingCategories(false);
      });
  }, []);

  // Cargar POS disponibles
  useEffect(() => {
    setLoadingPOS(true);
    POSService.getPOSList()
      .then((data) => {
        setPosList(data.filter((p) => p.activo));
        setLoadingPOS(false);
      })
      .catch(() => {
        setPosError("Error al cargar POS disponibles");
        setLoadingPOS(false);
      });
  }, []);

  /**
   * Valida un campo específico
   */
  const validateField = (field: keyof IProductFormData, value: any): string => {
    try {
      // Access the underlying schema using .inner or similar if productSchema is a ZodEffects
      const baseSchema = productSchema._def.schema || productSchema;
      const fieldSchema = (baseSchema as any).shape[field];
      fieldSchema.parse(value);
      return "";
    } catch (error) {
      if (error instanceof z.ZodError) {
        return error.errors[0]?.message || "";
      }
      return "";
    }
  };

  /**
   * Maneja el cambio en campos de texto
   */
  const handleInputChange = (field: keyof IProductFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Limpiar error si el campo se vuelve válido
    if (errors[field]) {
      const fieldError = validateField(field, value);
      if (!fieldError) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  /**
   * Maneja el cambio en campos numéricos
   */
  const handleNumberChange = (field: keyof IProductFormData, value: string) => {
    const numValue = value === "" ? 0 : parseFloat(value);
    setFormData((prev) => ({ ...prev, [field]: numValue }));

    // Limpiar error si el campo se vuelve válido
    if (errors[field]) {
      const fieldError = validateField(field, numValue);
      if (!fieldError) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  /**
   * Maneja el cambio en switches
   */
  const handleSwitchChange = (
    field: keyof IProductFormData,
    checked: boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  /**
   * Maneja el evento blur de los campos
   */
  const handleBlur = (field: keyof IProductFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    const value = formData[field];
    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  /**
   * Obtiene el estado visual del campo
   */
  const getFieldState = (
    field: keyof IProductFormData
  ): "default" | "error" | "success" => {
    if (errors[field] && touched[field]) return "error";
    if (formData[field] && !errors[field] && touched[field]) return "success";
    return "default";
  };

  /**
   * Renderiza el icono del campo según su estado
   */
  const renderFieldIcon = (field: keyof IProductFormData) => {
    const state = getFieldState(field);

    if (state === "error") {
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
    if (state === "success") {
      return <CheckCircle className="h-4 w-4 text-primary" />;
    }
    return <Package className="h-4 w-4 text-muted-foreground" />;
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos los campos como tocados
    const allFields = Object.keys(formData) as (keyof IProductFormData)[];
    const newTouched = allFields.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(newTouched);

    // Validar todos los campos
    const newErrors: Record<string, string> = {};
    allFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    // Si no hay errores, enviar el formulario
    if (Object.keys(newErrors).length === 0) {
      // Ajusta el payload para enviar el category_id como número
      const submitData = {
        ...formData,
        categoria: formData.categoria, // eliminamos el campo categoria string
        category_id: Number(formData.categoria), // agregamos el id numérico
        imageFile: imageFile || undefined,
      };
      console.log("submitData", submitData);
      onSubmit(submitData as any); // el backend espera category_id
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Nombre del Producto */}
      <div className="space-y-2">
        <Label htmlFor="nombre" className="text-sm font-medium text-foreground">
          Nombre del Producto *
        </Label>
        <div className="relative">
          <Input
            id="nombre"
            value={formData.nombre}
            onChange={(e) => handleInputChange("nombre", e.target.value)}
            onBlur={() => handleBlur("nombre")}
            placeholder="Ej: Pollo a la Plancha"
            disabled={isLoading}
            className={`h-12 text-base pr-10 ${
              getFieldState("nombre") === "error"
                ? "border-destructive focus:border-destructive focus:ring-destructive"
                : getFieldState("nombre") === "success"
                ? "border-primary focus:border-primary focus:ring-primary"
                : ""
            }`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {renderFieldIcon("nombre")}
          </div>
        </div>
        {errors.nombre && (
          <p className="text-sm text-destructive">{errors.nombre}</p>
        )}
      </div>

      {/* Descripción */}
      <div className="space-y-2">
        <Label
          htmlFor="descripcion"
          className="text-sm font-medium text-foreground"
        >
          Descripción *
        </Label>
        <Textarea
          id="descripcion"
          value={formData.descripcion}
          onChange={(e) => handleInputChange("descripcion", e.target.value)}
          onBlur={() => handleBlur("descripcion")}
          placeholder="Describe el producto..."
          disabled={isLoading}
          rows={4}
          className={`text-base min-h-[120px] ${
            getFieldState("descripcion") === "error"
              ? "border-destructive focus:border-destructive focus:ring-destructive"
              : getFieldState("descripcion") === "success"
              ? "border-primary focus:border-primary focus:ring-primary"
              : ""
          }`}
        />
        {errors.descripcion && (
          <p className="text-sm text-destructive">{errors.descripcion}</p>
        )}
      </div>

      {/* Precio */}
      <div className="space-y-2">
        <Label htmlFor="precio" className="text-sm font-medium text-foreground">
          Precio (CLP) *
        </Label>
        <div className="relative">
          <Input
            id="precio"
            type="number"
            value={formData.precio || ""}
            onChange={(e) => handleNumberChange("precio", e.target.value)}
            onBlur={() => handleBlur("precio")}
            placeholder="0"
            min="0"
            step="100"
            disabled={isLoading}
            className={`h-12 text-base pr-10 ${
              getFieldState("precio") === "error"
                ? "border-destructive focus:border-destructive focus:ring-destructive"
                : getFieldState("precio") === "success"
                ? "border-primary focus:border-primary focus:ring-primary"
                : ""
            }`}
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {renderFieldIcon("precio")}
          </div>
        </div>
        {errors.precio && (
          <p className="text-sm text-destructive">{errors.precio}</p>
        )}
      </div>

      {/* Categoría */}
      <div className="space-y-2">
        <Label
          htmlFor="categoria"
          className="text-sm font-medium text-foreground"
        >
          Categoría *
        </Label>
        <Select
          value={formData.categoria}
          onValueChange={(value) => {
            console.log("value selected", value);
            setFormData((prev) => ({ ...prev, categoria: value }));
            if (errors.categoria) {
              const fieldError = validateField("categoria", value);
              if (!fieldError) {
                setErrors((prev) => ({ ...prev, categoria: "" }));
              }
            }
          }}
          disabled={isLoading || loadingCategories}
        >
          <SelectTrigger className="h-12 text-base">
            <SelectValue
              placeholder={loadingCategories ? "Cargando..." : "Selecciona una categoría"}
            >
              {categories.find(c => String(c.id) === formData.categoria)?.nombre}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={String(cat.id)}>
                {cat.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {categoriesError && (
          <p className="text-sm text-destructive">{categoriesError}</p>
        )}
        {errors.categoria && (
          <p className="text-sm text-destructive">{errors.categoria}</p>
        )}
      </div>

      {/* Subida de Imagen */}
      <ImageUpload
        value={formData.imagen}
        onChange={(value) => handleInputChange("imagen", value)}
        onFileChange={(file) => setImageFile(file)}
        label="Imagen del Producto"
        required={false}
        maxSize={5}
        acceptedTypes={["image/jpeg", "image/png", "image/webp"]}
      />

      {/* URL de la Imagen (alternativa) */}
      <div className="space-y-2">
        <Label
          htmlFor="imagen-url"
          className="text-sm font-medium text-foreground"
        >
          URL de la Imagen (Alternativa)
        </Label>
        <Input
          id="imagen-url"
          type="url"
          value={formData.imagen}
          onChange={(e) => handleInputChange("imagen", e.target.value)}
          placeholder="https://ejemplo.com/imagen.jpg"
          disabled={isLoading}
          className="h-12 text-base"
        />
        <p className="text-xs text-muted-foreground">
          Opcional. Puedes subir una imagen o proporcionar una URL externa.
        </p>
      </div>

      {/* Tipo de Producto (Single/Pack) */}
      <div className="space-y-2">
        <Label htmlFor="type" className="text-sm font-medium text-foreground">
          Tipo de Producto *
        </Label>
        <Select
          value={formData.type}
          onValueChange={(value) => {
            setFormData((prev) => ({ ...prev, type: value as ('single' | 'pack'), pack_size: value === 'single' ? null : (prev.pack_size || 1) }));
            handleBlur('type');
          }}
          disabled={isLoading}
        >
          <SelectTrigger className="h-12 text-base">
            <SelectValue placeholder="Selecciona el tipo de producto">
              {formData.type === 'single' ? 'Producto Individual' : 'Pack de Productos'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Producto Individual</SelectItem>
            <SelectItem value="pack">Pack de Productos</SelectItem>
          </SelectContent>
        </Select>
        {errors.type && (
          <p className="text-sm text-destructive">{errors.type}</p>
        )}
      </div>

      {/* Tamaño del Pack (Condicional) */}
      {formData.type === 'pack' && (
        <div className="space-y-2">
          <Label htmlFor="pack_size" className="text-sm font-medium text-foreground">
            Tamaño del Pack *
          </Label>
          <div className="relative">
            <Input
              id="pack_size"
              type="number"
              value={formData.pack_size || ''}
              onChange={(e) => handleNumberChange("pack_size", e.target.value)}
              onBlur={() => handleBlur("pack_size")}
              placeholder="1"
              min="1"
              step="1"
              disabled={isLoading}
              className={`h-12 text-base pr-10 ${errors.pack_size ? 'border-destructive focus:border-destructive focus:ring-destructive' : ''}`}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {renderFieldIcon("pack_size")}
            </div>
          </div>
          {errors.pack_size && (
            <p className="text-sm text-destructive">{errors.pack_size}</p>
          )}
        </div>
      )}

      {/* POS disponibles */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-foreground">
          POS donde estará disponible
        </Label>
        <div className="p-3 border border-border rounded-md">
          {loadingPOS ? (
            <p className="text-sm text-muted-foreground">Cargando POS...</p>
          ) : posError ? (
            <p className="text-sm text-destructive">{posError}</p>
          ) : posList.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay POS disponibles</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {posList.map((pos) => {
                const checked = (formData.posIds || []).includes(pos.id);
                return (
                  <label key={pos.id} className="flex items-center space-x-3">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(isChecked) => {
                        setFormData((prev) => {
                          const current = prev.posIds || [];
                          const next = isChecked
                            ? Array.from(new Set([...current, pos.id]))
                            : current.filter((id) => id !== pos.id);
                          return { ...prev, posIds: next };
                        });
                      }}
                      disabled={isLoading}
                    />
                    <span className="text-sm text-foreground">{pos.nombre}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Selecciona uno o más puntos de venta donde se mostrará este producto.</p>
      </div>

      {/* Disponible */}
      <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
        <Switch
          id="disponible"
          checked={formData.disponible}
          onCheckedChange={(checked) =>
            handleSwitchChange("disponible", checked)
          }
          disabled={isLoading}
        />
        <Label
          htmlFor="disponible"
          className="text-sm font-medium text-foreground"
        >
          Disponible
        </Label>
      </div>

      {/* Información adicional */}
      <div className="p-4 bg-muted rounded-lg border border-border">
        <p className="text-sm text-muted-foreground">
          <strong>Nota:</strong> Los campos marcados con * son obligatorios.
        </p>
      </div>

      {/* Botón de envío */}
      <Button
        type="submit"
        disabled={isLoading}
        className="w-full h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90"
      >
        {isLoading ? "Guardando..." : "Guardar Producto"}
      </Button>
    </form>
  );
};

export default ProductForm;
